import React from "react";
import { useTranslation } from "react-i18next";
import { ButtonCategory } from "@components/atoms/Button/types";
import { messageType } from "@components/molecules/PopUp/types";
import { ProductCategory } from "@utils/constants";
import AskSupplierModal from "@components/molecules/AskSupplier";
import IframeRequestModal from "@components/molecules/iFrameRequestPopUp";
import ConfirmationPopup from "@components/atoms/ConfirmationPopUp";
import BMHModal from "@components/molecules/BMH/SlideSheet";
import OptionsModal from "../utils/OptionsModal";

interface PdpBottomActionsProps {
  // Data
  itemDetail: any;
  productType: ProductCategory;
  priceMode: number;

  // Modals state
  modals: {
    showBMHModal: boolean;
    showButtonsOptions: boolean;
    showAskSupplierModal: boolean;
    iFramePopUp: boolean;
    showSuccessPopup: boolean;
    bmhPopUp: boolean;
    showStoneToSettingPopup: boolean;
  };
  toggleModal: (modalName: string, isOpen: boolean) => void;

  // BMH Modal props
  selectedTabSlideSheet: any;
  itemQuantity: number;
  setItemQuantity: (quantity: number) => void;
  jcSelectedOption: any;
  personalization: any;

  // Options Modal props
  requestOptionsButton: any;
  handleBottomButtonActions: (action: string) => void;

  // Settings
  settings: any;
  userInfo: any;

  onStoneToSettingPopupDismiss: () => void;
  onStoneToSettingPopupCancel: () => void;
}

const PdpBottomActions = ({
  itemDetail,
  productType,
  priceMode,
  modals,
  toggleModal,
  selectedTabSlideSheet,
  itemQuantity,
  setItemQuantity,
  jcSelectedOption,
  personalization,
  requestOptionsButton,
  handleBottomButtonActions,
  settings,
  userInfo,
  onStoneToSettingPopupDismiss,
  onStoneToSettingPopupCancel,
}: PdpBottomActionsProps) => {
  const { t } = useTranslation();

  const getButtonCategory = () => {
    if (itemDetail?.type === "diamond") return ButtonCategory.diam;
    if (itemDetail?.type === "gemstone") return ButtonCategory.gem;
    if (itemDetail?.type === "jewelry") return ButtonCategory.jewelry;
    return ButtonCategory.lab;
  };

  return (
    <>
      <BMHModal
        isOpen={modals.showBMHModal}
        onClose={() => toggleModal("showBMHModal", false)}
        title={""}
        itemDetail={itemDetail}
        initialSelectedOption={selectedTabSlideSheet}
        initialSelectedQuantity={itemQuantity}
        onSelectedQuantityChange={setItemQuantity}
        productType={productType ?? ProductCategory.DEFAULT}
        externalPriceMode={priceMode}
        additionalAttributes={{
          config: jcSelectedOption?.current?.option_value,
          personalization: personalization ?? {},
        }}
        fromReviewPage={false}
      />
      <OptionsModal
        isOpen={modals.showButtonsOptions}
        onClose={() => toggleModal("showButtonsOptions", false)}
        options={Object.fromEntries(
          Object.entries(requestOptionsButton?.options || {})
            .filter(([key]) => {
              if (
                itemDetail?.company_identifier &&
                userInfo?.companyIdentifierEnabled()
              ) {
                return key === "request_item" || key === "ask_supplier";
              }
              return true;
            })
            .sort(([, a], [, b]) => (a as any).order - (b as any).order)
        )}
        onOptionSelect={(optionName) => {
          toggleModal("showButtonsOptions", false);
          handleBottomButtonActions(optionName);
        }}
        t={t}
        priceMode={priceMode}
        title={requestOptionsButton?.modalTitle}
        isMyInventory={
          itemDetail?.company_identifier && userInfo.companyIdentifierEnabled()
        }
      />
      <AskSupplierModal
        isOpen={modals.showAskSupplierModal}
        onClose={() => toggleModal("showAskSupplierModal", false)}
        itemDetail={itemDetail}
        onSuccess={() => toggleModal("showSuccessPopup", true)}
      />
      <IframeRequestModal
        isOpen={modals.iFramePopUp}
        onClose={() => toggleModal("iFramePopUp", false)}
        itemDetail={itemDetail}
        onSuccess={() => toggleModal("showSuccessPopup", true)}
        productType={productType}
      />
      <ConfirmationPopup
        title={t("askSupplier.popUp.title.text")}
        htmlMessage={t("askSupplier.popUp.discription.text")}
        primaryButtonTitle={t("askSupplier.popUp.button.text")}
        primaryButtonAction={() => {
          toggleModal("showSuccessPopup", false);
        }}
        category={getButtonCategory()}
        messageType={messageType.Success}
        colorKey={"core-05"}
        opened={modals.showSuccessPopup}
      />
      <ConfirmationPopup
        title={t("bmh.canRequestPopUp.title.text")}
        htmlMessage={t("bmh.canRequestPopUp.disciption.text", {
          x: settings.organization.emails.support
            ? settings.organization.emails.support
            : "support@vdbapp.com",
        })}
        primaryButtonTitle={t("bmh.canRequestPopUp.button.title.text")}
        primaryButtonAction={() => toggleModal("bmhPopUp", false)}
        category={getButtonCategory()}
        messageType={messageType.Generic}
        opened={modals.bmhPopUp}
      />
      <ConfirmationPopup
        title={t(
          "pdp.stoneToSetting.popup.title.text",
          "Change Center Stone Size?"
        )}
        htmlMessage={t(
          "pdp.stoneToSetting.popup.description.text",
          "To choose this size, you'll need to select a new stone."
        )}
        primaryButtonTitle={t("pdp.stoneToSetting.popup.button.text", "Yes")}
        primaryButtonAction={onStoneToSettingPopupDismiss}
        secondaryButtonTitle={t(
          "pdp.stoneToSetting.popup.button.no.text",
          "No"
        )}
        secondaryButtonAction={onStoneToSettingPopupCancel}
        category={getButtonCategory()}
        messageType={messageType.Generic}
        opened={modals.showStoneToSettingPopup}
      />
    </>
  );
};

export default PdpBottomActions;
